// src\modules\Products\List\components\modals\AddStockToLocationModal.tsx
import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { Button } from '@shared/components/ui/button';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { DynamicCombobox } from '@shared/components/ui/DynamicCombobox';
import { apiClientWH } from '@shared/services';
import { useQueryClient } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductsApiAdapter } from '../../../CreateEdit/adapters/ProductsApiAdapter';
import { BatchSelect } from '@modules/Batches/components/BatchSelect';
import { useAvailableLocationsSearch } from '../../hooks/useAvailableLocationsSearch';

export interface AddStockToLocationModalRef {
  open: (onConfirm?: () => void) => void;
}

interface AddStockToLocationModalProps {
  productId: string | number | null;
  refetch?: () => void;
}

export const AddStockToLocationModal = forwardRef<AddStockToLocationModalRef, AddStockToLocationModalProps>(
  ({ productId, refetch }, ref) => {
    const { t } = useTranslation('productsModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const confirmRef = useRef<() => void>(() => {});
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [isOpen, setIsOpen] = useState(false);
    const [ubicacion, setUbicacion] = useState('');
    const [unidades, setUnidades] = useState(0);
    const [motivo, setMotivo] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);

    const { searchFn, hasMore } = useAvailableLocationsSearch(productId, isOpen);

    useImperativeHandle(ref, () => ({
      open: (onConfirm) => {
        confirmRef.current = onConfirm || (() => {});
        setUbicacion('');
        setUnidades(0);
        setMotivo('');
        setSelectedBatchId(null);
        setIsOpen(true);
        modalRef.current?.openModal();
      },
    }));

    const handleCloseModal = () => {
      setIsOpen(false);
    };

    const handleConfirm = async () => {
      if (!productId) {
        toast({
          title: t('toasts.error_title'),
          description: t('toasts.stock_adjust_error'),
          variant: 'destructive',
        });
        return;
      }
      if (!ubicacion || unidades <= 0) {
        toast({
          title: t('toasts.error_title'),
          description: t('modal.adjust_stock_note'),
          variant: 'destructive',
        });
        return;
      }

      setSubmitting(true);
      try {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new ProductsApiAdapter(httpClient);
        await adapter.adjustStock({
          locationId: ubicacion,
          productId: productId,
          quantity: unidades,
          description: motivo,
          batchId: selectedBatchId ?? undefined,
        });

        toast({
          title: t('toasts.stock_adjust_success'),
          description: t('modal.adjust_stock_note'),
          variant: 'success',
        });

        await queryClient.invalidateQueries({
          queryKey: ['locations-by-product', productId],
        });

        await queryClient.invalidateQueries({
          queryKey: ['product-stock-location-ids', productId],
        });

        if (refetch) {
          refetch();
        }

        confirmRef.current();
        modalRef.current?.closeModal();
      } catch (error: any) {
        const data = error?.response?.data;
        const apiMessage =
          data?.message ||
          data?.error?.message ||
          (Array.isArray(data?.errors) && data.errors[0]?.message) ||
          data?.error_description ||
          error?.message;
        toast({
          title: t('toasts.stock_adjust_error'),
          description: apiMessage || t('toasts.error_title'),
          variant: 'destructive',
        });
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('modal.add_stock_to_location_title')}
        type="default"
        width="lg"
        fullScreenMobile={true}
        onCloseModal={handleCloseModal}
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto space-y-6 mt-1">
            <div className="grid grid-cols-1 md:grid-cols-10 gap-4 items-end">
              <div className="md:col-span-7">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm text-text">{t('modal.select_location_label')}</label>
                  <InfoTooltip
                    text={t('modal.select_location_info')}
                    iconClassName="w-3.5 h-3.5"
                    dataTestId="ubicacion-tooltip"
                  />
                </div>
                <DynamicCombobox
                  value={ubicacion}
                  setValue={setUbicacion}
                  placeholder={t('modal.select_location_placeholder')}
                  searchFn={searchFn}
                  hasMore={hasMore}
                  dataTestId="combobox-ubicacion"
                  emptyMessage={t('modal.locations_search_empty')}
                  loadingMessage={t('modal.locations_search_loading')}
                />
              </div>
              <div className="md:col-span-3 max-w-[150px]">
                <label className="block text-sm mb-2 text-text">{t('modal.units_label')}</label>
                <StepperInput
                  value={unidades}
                  onChange={setUnidades}
                  min={0}
                  max={99999}
                  data-testid="stock-units-stepper"
                />
              </div>
            </div>

            {productId && (
              <BatchSelect productId={Number(productId)} value={selectedBatchId} onChange={setSelectedBatchId} />
            )}
            <div>
              <label className="block text-sm mb-2 text-text">{t('modal.reason_label')}</label>
              <Input
                placeholder={t('modal.reason_placeholder')}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                data-testid="stock-reason-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              size="lg"
              onClick={() => modalRef.current?.closeModal()}
              data-testid="cancel-stock-modal-button"
            >
              {t('modal.cancel')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleConfirm}
              disabled={!ubicacion || unidades <= 0 || submitting}
              data-testid="confirm-stock-modal-button"
            >
              {t('modal.add')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

AddStockToLocationModal.displayName = 'AddStockToLocationModal';
